<script>
import { fetchAssetPlugins } from '@apis/admin/asset'
import AssetPluginInfo from '@components/admin/asset-plugin-info'
import AdminLayout from '@layouts/admin-main'
import PageNavigation from '@components/admin/page-navigation'

export default {
  page: {
    title: 'AssetPluginList',
    meta: [{ name: 'description', content: 'AssetPluginList' }],
  },
  components: { AdminLayout, AssetPluginInfo, PageNavigation },
  data() {
    return {
      asset_plugins: [],
      target_asset_plugin_seq: 0,
      show_asset_plugin_info: false,
      total_count: 0,
      page: 1,
      total_page: 0,
      page_navigation: {},
      search_params: {},
      loading: false,
    }
  },
  watch: {
    $route(to, from) {
      this.page = this.$route.query.page || 1
      this.loadAssetPlugins().catch((e) => this.$toasted.error(e.message))
    },
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.loading = true

      this.page = this.$route.query.page || 1
      this.search_params = { ...this.$route.query }
      Promise.all([this.loadAssetPlugins()])
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadAssetPlugins() {
      const ret_obj = await fetchAssetPlugins({
        ...this.$route.query,
        no_paging: 0,
      })

      this.asset_plugins = ret_obj.data
      this.total_count = ret_obj.total_count
      this.total_page = ret_obj.total_page
      this.page_navigation = ret_obj.page_navigation
    },
    handleCreateAssetPlugin() {
      this.show_asset_plugin_info = true
      this.target_asset_plugin_seq = null
    },
    handleUpdateAssetPlugin({ seq }) {
      this.show_asset_plugin_info = true
      this.target_asset_plugin_seq = seq
    },
    handleAssetPluginSubmit(seq) {
      this.init()
      this.target_asset_plugin_seq = seq
    },
    search() {
      this.$router.push({ query: this.search_params })
    },
    clearAndSearch() {
      this.search_params = {}
      this.search()
    },
  },
}
</script>

<template>
  <AdminLayout v-loading.fullscreen="loading">
    <h3 :class="$style.xeAdmin">{{ $t('admin.search') }}</h3>
    <form @submit.prevent="search">
      <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
        <colgroup>
          <col width="20%" />
          <col width="30%" />
          <col width="20%" />
          <col width="30%" />
        </colgroup>
        <tbody>
          <tr>
            <th scope="col">
              <div>
                <select
                  v-model="search_params.search_target"
                  :class="$style.fullWidth"
                >
                  <option
                    v-for="val in [
                      [undefined, $t('admin.search_target')],
                      ['title', $t('asset_plugin_list.title')],
                      ['regdate_more', $t('admin.login_log_regdate_more')],
                      ['regdate_less', $t('admin.login_log_regdate_less')],
                    ]"
                    :key="val[1]"
                    :value="val[0]"
                    >{{ val[1] }}</option
                  >
                </select>
              </div>
            </th>
            <td>
              <input
                v-model="search_params.search_keyword"
                :placeholder="$t('admin.search_word')"
                type="text"
                :class="[$style.inputTypeText, $style.fullWidth]"
              />
            </td>
            <td colspan="2">
              <ElCheckbox
                v-model="search_params.allow_scan"
                :false-label="undefined"
                true-label="Y"
                >{{ $t('asset_plugin_list.scan_status') }}</ElCheckbox
              >
            </td>
          </tr>

          <tr>
            <th colspan="4" :class="$style.button">
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="clearAndSearch"
                ><span>{{ $t('admin.reset') }}</span></a
              >
              &nbsp;
              <span :class="[$style.button, $style.black]"
                ><input type="submit" :value="$t('admin.submit')"
              /></span>
            </th>
          </tr>
        </tbody>
      </table>
    </form>
    <h3 :class="$style.xeAdmin">{{ $t('asset_plugin_list.plug_ins') }}</h3>
    <table cellspacing="0" cellpadding="0" :class="$style.colTable">
      <caption v-if="!loading"
        >Total {{ total_count }}, Page {{ page }}/{{ total_page }}</caption
      >
      <thead>
        <tr>
          <th scope="col"
            ><div>{{ $t('admin.num') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.plugin_title') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.plugin') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.start_block') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.allow_scan') }}</div></th
          >
          <th scope="col">
            <div :class="$style.tRight">
              <a
                href="#"
                :class="[$style.button, $style.black]"
                @click.prevent="handleCreateAssetPlugin"
                ><span>{{ $t('admin.add') }}</span></a
              >
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="asset_plugin in asset_plugins" :key="asset_plugin.seq">
          <td :class="$style.tCenter">{{ asset_plugin._no }}</td>
          <td :class="[$style.tCenter, $style.wide]">{{
            asset_plugin.title
          }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            asset_plugin.plugin
          }}</td>
          <td :class="[$style.tRight]">{{ asset_plugin.start_block }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            asset_plugin.allow_scan
          }}</td>
          <td :class="[$style.tRight, $style.nowrap]">
            <a
              href="#"
              :class="[$style.button]"
              @click.prevent="handleUpdateAssetPlugin(asset_plugin)"
              ><span>{{ $t('admin.modify') }}</span></a
            >
          </td>
        </tr>
        <tr v-if="!asset_plugins.length">
          <td :colspan="5" :class="$style.tCenter">{{
            $t('admin.no_asset_plugin')
          }}</td>
        </tr>
      </tbody>
    </table>
    <PageNavigation
      v-if="!loading"
      :total_count="total_count"
      :total_page="page_navigation.total_page"
      :cur_page="page"
      :page_count="page_navigation.page_count"
    ></PageNavigation>
    <ElDialog
      :title="
        target_asset_plugin_seq
          ? $t('admin.asset_plugin_modify')
          : $t('admin.asset_plugin_enroll')
      "
      :visible.sync="show_asset_plugin_info"
    >
      <AssetPluginInfo
        v-if="show_asset_plugin_info"
        :key="target_asset_plugin_seq"
        :asset_plugin_seq="target_asset_plugin_seq"
        @submit="handleAssetPluginSubmit"
      ></AssetPluginInfo>
    </ElDialog>
  </AdminLayout>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';
</style>
